import { EMPTY, merge, Observable, Subject, Subscription } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { Database } from '@angular/fire/database';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { Nullish } from 'src/lib/fliko/types/typeish.type';
import { AsyncHlp } from '../helpers/async.helper';
import { DatabaseFactory } from './database/database.factory';
import { NulCol } from './database/dbCollection';
import { FireDbService } from './fireDb.service';

@Injectable({ providedIn: 'root' })
export class DatabaseService implements OnDestroy {
  private commonDbRef!: Database;
  private estateShardDbRef: Database | null = null;
  private companyShardDbRef: Database | null = null;

  public readonly commonFactory = new DatabaseFactory();
  public readonly estateFactory = new DatabaseFactory();
  public readonly companyFactory = new DatabaseFactory();

  // HACK: Friend-like declaration? https://github.com/microsoft/TypeScript/issues/7692
  public static getCommonDbRef = (srv: DatabaseService) => srv.commonDbRef;

  private contextDb$: Observable<NulCol<string>> = EMPTY;
  private subscription$ = new Subscription();
  private userSessionEnded$ = new Subject<void>();
  private contextChangeIds = { estate: 0, company: 0 };
  private contextReadCancelled$ = { estate: new Subject<void>(), company: new Subject<void>() };

  ngOnDestroy(): void {
    this.initializeWithUser(null);
    this.subscription$.unsubscribe();
    this.userSessionEnded$.complete();
    this.contextReadCancelled$.estate.complete();
    this.contextReadCancelled$.company.complete();
  }

  /**
   * Initialize commonFactory or de-initialize all factories if null
   * @param {string | null} userId
   * @warning used ONLY by ContextService (omit circular)
   */
  public initializeWithUser(userId: string | null): void {
    this.userSessionEnded$.next();
    this.subscription$.unsubscribe();
    this.subscription$ = new Subscription();
    this.contextDb$ = EMPTY;
    this.contextChangeIds.estate += 1;
    this.contextChangeIds.company += 1;
    this.contextReadCancelled$.estate.next();
    this.contextReadCancelled$.company.next();
    this.estateFactory.finalize();
    this.companyFactory.finalize();

    if (userId) {
      // ? lazy initializing, because we need to wait some time for database to initialize
      if (!this.commonDbRef) this.commonDbRef = FireDbService.getDbRef(FireDbService.commonDbName);

      this.commonFactory.initialize(userId, this.commonDbRef, FireDbService.commonDbName);
      this.contextDb$ = this.commonFactory
        .getCollectionReadOnly<string>('contextDb')
        .getObject()
        .pipe(takeUntil(this.userSessionEnded$), shareReplay({ bufferSize: 1, refCount: true }));
      // Keep one live shard map for the signed-in user so module guards reuse the latest response.
      this.subscription$.add(this.contextDb$.subscribe());
    } else {
      this.commonFactory.finalize();
    }
  }

  /**
   * Initialize estateFactory or de-initialize if null
   * @param {string | null} estateId
   * @param cancel$ Cancels the pending selection when its navigation is superseded
   * @warning used ONLY by ContextService (omit circular)
   */
  public changeEstate(estateId: string | null, cancel$?: Observable<unknown>): Promise<void> {
    return this.changeEstateCompany('estate', estateId, cancel$);
  }

  /**
   * Initialize companyFactory or de-initialize if null
   * @param {string | null} companyId
   * @warning used ONLY by ContextService (omit circular)
   */
  public changeCompany(companyId: string | null): Promise<void> {
    return this.changeEstateCompany('company', companyId);
  }

  /**
   * Get database name from estate or company id as Promise
   * @param {string} id estate or company
   * @return {Promise<string | Nullish>}
   */
  public async getDbNameFromId(id: string): Promise<string | Nullish> {
    return AsyncHlp.onFirst(this.contextDb$.pipe(map((value) => ObjHlp.get(value, id))));
  }

  private async changeEstateCompany(type: 'estate' | 'company', id: string | null, cancel$?: Observable<unknown>): Promise<void> {
    const changeId = ++this.contextChangeIds[type];
    this.contextReadCancelled$[type].next();
    const factory = this[`${type}Factory`];
    const shardDbKey = `${type}ShardDbRef` as const;

    factory.finalize();
    if (!id) return;

    let cancelled = false;
    const cancellation = cancel$?.subscribe(() => (cancelled = true));
    try {
      const contextDb = await AsyncHlp.onFirst(
        this.contextDb$.pipe(takeUntil(merge(this.contextReadCancelled$[type], cancel$ ?? EMPTY)))
      );
      if (cancelled || changeId !== this.contextChangeIds[type]) return;
      const dbName = ObjHlp.get(contextDb, id);
      if (!dbName) throw new Error(`${type} '${id}' don't have assigned database!`);

      if (dbName !== this[shardDbKey]?.app.name) {
        const shardDbRef = FireDbService.getDbRef(dbName);
        if (!shardDbRef) throw new Error(`Database '${dbName}' not exist!`);
        this[shardDbKey] = shardDbRef;
      }
      factory.initialize(id, this[shardDbKey]!, shardDbKey);
    } finally {
      cancellation?.unsubscribe();
    }
  }
}
